import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Card, Col, Grid, Row, Typography, Button, Tag, Skeleton, Empty, Flex, Tooltip, theme } from 'antd';
import {
  DashboardOutlined,
  CalendarOutlined,
  MessageOutlined,
  UserOutlined,
  ClockCircleOutlined,
  VideoCameraOutlined,
  ArrowRightOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  useAppointmentStatsQuery,
  useListAppointmentsQuery,
  useListCustomerRescheduleRequestsQuery
} from '@/api/mcp-appointments';
import {
  useGetMyUnifiedThreadsQuery,
  useGetChatThreadsQuery
} from '@/api/client-communications';
import type { Appointment } from '@/api/mcp-appointments';
import {
  formatAppointmentTimeRange,
  getAppointmentStatusLabel,
  getCalendarAppointmentResourceLabel,
  getStatusBackgroundColor,
  getStatusTagColor
} from '@/components/appointments/calendar/calendarUtils';
import PageTransition from '@/components/ui/PageTransition';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { StatCardGrid } from '@/components/ui/StatCardGrid';
import { ClientScopeEmpty } from '@/components/client-portal/ClientScopeEmpty';
import { SectionTitle } from '@/components/dashboard/SectionTitle';
import { ClientListItemCard } from '@/components/client-portal/ClientListItemCard';
import { QuickActionCard } from '@/components/dashboard/QuickActionCard';
import { useClientPortalScope } from '@/hooks/useClientPortalScope';

export const Route = createFileRoute('/client/')({
  component: ClientDashboardPage
});

const { Text } = Typography;

function appointmentTitle(appointment: Appointment, fallback: string): string {
  return (
    appointment.service?.name ||
    getCalendarAppointmentResourceLabel(appointment) ||
    appointment.anonymousName ||
    fallback
  );
}

function ClientDashboardPage() {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const { isAdmin, isCustomer, adminScopeReady, selectedCustomerId, selectedClientId } = useClientPortalScope();
  const appointmentParams = useMemo(
    () => ({
      includeBookingDetails: true,
      startDate: dayjs().startOf('day').toISOString(),
      ...(isAdmin && selectedCustomerId ? { userId: selectedCustomerId } : {})
    }),
    [isAdmin, selectedCustomerId]
  );

  const { data: stats, isLoading: statsLoading } = useAppointmentStatsQuery(
    isAdmin && selectedCustomerId ? { userId: selectedCustomerId } : undefined,
    adminScopeReady
  );
  const { data: appointments, isLoading: appointmentsLoading } = useListAppointmentsQuery(
    appointmentParams,
    adminScopeReady
  );
  const { data: rescheduleRequestsData, isLoading: rescheduleRequestsLoading } =
    useListCustomerRescheduleRequestsQuery(undefined, adminScopeReady && isCustomer);
  const myThreadsQuery = useGetMyUnifiedThreadsQuery(isCustomer);
  const adminThreadsQuery = useGetChatThreadsQuery(
    { userId: selectedCustomerId ?? undefined },
    isAdmin && adminScopeReady
  );

  const threadsQuery = isCustomer ? myThreadsQuery : adminThreadsQuery;
  const threadsData = threadsQuery.data;
  const threadsLoading = threadsQuery.isPending || threadsQuery.isLoading;

  const upcomingAppointments = useMemo(() => {
    const list = Array.isArray(appointments) ? appointments : [];
    return list
      .filter((a) => a.status !== 'CANCELLED' && a.status !== 'COMPLETED' && a.status !== 'NO_SHOW')
      .filter((a) => dayjs(a.startTime).isAfter(dayjs().subtract(1, 'minute')))
      .sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf())
      .slice(0, 5);
  }, [appointments]);

  const openThreads = threadsData?.threads?.length ?? 0;

  const changeRequests = useMemo(() => {
    const reschedule = (rescheduleRequestsData?.requests ?? []).filter((request) => request.status === 'PENDING');
    return reschedule
      .sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf())
      .slice(0, 8);
  }, [rescheduleRequestsData]);

  const pendingChangeRequestCount = changeRequests.filter((request) => request.status === 'PENDING').length;

  const isLoading =
    !adminScopeReady ||
    statsLoading ||
    appointmentsLoading ||
    threadsLoading ||
    (isCustomer && rescheduleRequestsLoading);

  const statCards = [
    {
      key: 'week',
      title: t('client.stats.upcomingThisWeek'),
      value: stats?.upcomingThisWeek ?? 0,
      icon: <CalendarOutlined />,
      color: token.colorPrimary,
      onClick: () => navigate({ to: '/client/calendar' })
    },
    {
      key: 'today',
      title: t('client.stats.upcomingToday'),
      value: stats?.upcomingToday ?? 0,
      icon: <ClockCircleOutlined />,
      color: token.colorSuccess,
      onClick: () => navigate({ to: '/client/calendar' })
    },
    {
      key: 'pendingRequests',
      title: t('client.stats.pendingRequests'),
      value: pendingChangeRequestCount,
      icon: <ThunderboltOutlined />,
      color: token.colorWarning,
      onClick: () => navigate({ to: '/client/calendar' })
    },
    {
      key: 'threads',
      title: t('client.stats.openThreads'),
      value: openThreads,
      icon: <MessageOutlined />,
      color: token.purple,
      onClick: () => navigate({ to: '/client/threads' })
    }
  ];

  const quickActions = [
    {
      key: 'calendar',
      title: t('client.quickActions.viewCalendar'),
      icon: <CalendarOutlined />,
      color: token.colorPrimary,
      onClick: () => navigate({ to: '/client/calendar' })
    },
    {
      key: 'messages',
      title: t('client.quickActions.messages'),
      icon: <MessageOutlined />,
      color: token.purple,
      onClick: () => navigate({ to: '/client/threads' })
    },
    {
      key: 'profile',
      title: t('client.quickActions.profile'),
      icon: <UserOutlined />,
      color: token.cyan,
      onClick: () => navigate({ to: '/profile' })
    }
  ];

  /** Formats an appointment start (and optional end) in the active UI language. */
  const formatWhen = (start: string, end?: string | null) => formatAppointmentTimeRange(start, end, i18n.language);

  if (isAdmin && !adminScopeReady) {
    return <ClientScopeEmpty description={t('client.scopeRequired.dashboard')} />;
  }

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader icon={<DashboardOutlined />} title={t('client.title')} description={t('client.subtitle')} />

      <StatCardGrid>
        {statCards.map((stat) => (
          <StatCard
            key={stat.key}
            label={stat.title}
            value={stat.value}
            icon={stat.icon}
            color={stat.color}
            loading={isLoading}
            onClick={stat.onClick}
          />
        ))}
      </StatCardGrid>

      <Flex vertical gap={token.marginLG}>
        <Card
          title={
            <SectionTitle
              icon={<CalendarOutlined />}
              title={t('client.upcomingAppointments.title')}
              count={appointmentsLoading ? undefined : upcomingAppointments.length}
            />
          }
          extra={
            <Link to="/client/calendar">
              {screens.sm ? (
                <Button type="link" size="small" icon={<ArrowRightOutlined />} iconPlacement="end">
                  {t('client.upcomingAppointments.viewCalendar')}
                </Button>
              ) : (
                <Tooltip title={t('client.upcomingAppointments.viewCalendar')}>
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowRightOutlined />}
                    aria-label={t('client.upcomingAppointments.viewCalendar')}
                  />
                </Tooltip>
              )}
            </Link>
          }
        >
          {appointmentsLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : upcomingAppointments.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('client.upcomingAppointments.empty')} />
          ) : (
            <Flex vertical gap={token.marginXS}>
              {upcomingAppointments.map((item) => {
                const isVideo = item.format === 'VIDEO';
                return (
                  <ClientListItemCard
                    key={item.id}
                    accentColor={getStatusBackgroundColor(item.status)}
                    title={appointmentTitle(item, t('client.upcomingAppointments.anonymous'))}
                    onClick={() => navigate({ to: '/client/calendar' })}
                    tags={
                      <>
                        <Tag color={getStatusTagColor(item.status)} style={{ marginInlineEnd: 0 }}>
                          {getAppointmentStatusLabel(item.status, tCommon)}
                        </Tag>
                        <Tag
                          color={isVideo ? 'blue' : 'green'}
                          icon={isVideo ? <VideoCameraOutlined /> : <TeamOutlined />}
                          style={{ marginInlineEnd: 0 }}
                        >
                          {isVideo ? t('client.upcomingAppointments.video') : t('client.upcomingAppointments.inPerson')}
                        </Tag>
                      </>
                    }
                  >
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                      <ClockCircleOutlined style={{ marginInlineEnd: token.marginXXS }} />
                      {formatWhen(item.startTime, item.endTime)}
                    </Text>
                  </ClientListItemCard>
                );
              })}
            </Flex>
          )}
        </Card>

        <Card
          title={
            <SectionTitle
              icon={<ThunderboltOutlined />}
              title={t('client.pendingRequests.title')}
              count={rescheduleRequestsLoading ? undefined : changeRequests.length}
              countColor="orange"
            />
          }
        >
          {rescheduleRequestsLoading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : changeRequests.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('client.pendingRequests.empty')} />
          ) : (
            <Flex vertical gap={token.marginXS}>
              {changeRequests.map((item) => {
                const relatedAppointment = (Array.isArray(appointments) ? appointments : []).find(
                  (entry) => entry.id === item.appointmentId
                );
                return (
                  <ClientListItemCard
                    key={item.id}
                    accentColor={token.colorWarning}
                    title={t('client.changeRequest.typeReschedule')}
                    onClick={() => navigate({ to: '/client/calendar' })}
                    tags={
                      <Tag color={getStatusTagColor(item.status)} style={{ marginInlineEnd: 0 }}>
                        {t(`client.pendingRequests.statuses.${item.status}`, { defaultValue: item.status })}
                      </Tag>
                    }
                  >
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                      <CalendarOutlined style={{ marginInlineEnd: token.marginXXS }} />
                      {relatedAppointment
                        ? formatWhen(relatedAppointment.startTime, relatedAppointment.endTime)
                        : item.appointmentId}
                    </Text>
                    {item.requestedStartTime ? (
                      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                        <ArrowRightOutlined style={{ marginInlineEnd: token.marginXXS }} />
                        {t('client.pendingRequests.requested')}: {formatWhen(item.requestedStartTime)}
                      </Text>
                    ) : null}
                  </ClientListItemCard>
                );
              })}
            </Flex>
          )}
        </Card>

        <Card
          title={<SectionTitle icon={<AppstoreOutlined />} title={t('client.quickActions.title')} />}
        >
          <Row gutter={[token.marginSM, token.marginSM]}>
            {quickActions.map((action) => (
              <Col xs={24} sm={12} lg={8} key={action.key}>
                <QuickActionCard
                  icon={action.icon}
                  title={action.title}
                  color={action.color}
                  onClick={action.onClick}
                />
              </Col>
            ))}
          </Row>
        </Card>
      </Flex>
    </PageTransition>
  );
}
